import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DatePicker } from './ui/date-picker';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Calendar, Pencil } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  active:   'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  trial:    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  returned: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  left:     'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  inactive: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const SeasonTimeline = ({ athleteId, canEdit }) => {
  const [loading, setLoading] = useState(true);
  const [links, setLinks] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [editing, setEditing] = useState(null); // link object being edited, or 'new'
  const [form, setForm] = useState({ season_id: '', status: 'active', join_date: '', leave_date: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  const load = async () => {
    try {
      const [lres, sres] = await Promise.all([
        axios.get(`${API}/api/athletes/${athleteId}/seasons`, h),
        axios.get(`${API}/api/seasons`, h),
      ]);
      setLinks(lres.data || []);
      setSeasons(sres.data || []);
    } catch {
      /* silent — card will show empty */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (athleteId) load(); }, [athleteId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = (link) => {
    setForm({
      season_id: link.season_id,
      status: link.status,
      join_date: link.join_date || '',
      leave_date: link.leave_date || '',
      notes: link.notes || '',
    });
    setEditing(link);
  };
  const openNew = () => {
    const unlinked = seasons.filter((s) => !links.some((l) => l.season_id === s.id));
    setForm({
      season_id: unlinked[0]?.id || seasons[0]?.id || '',
      status: 'active',
      join_date: '',
      leave_date: '',
      notes: '',
    });
    setEditing('new');
  };

  const submit = async () => {
    if (!form.season_id) {
      toast.error('Pick a season.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        status: form.status,
        join_date: form.join_date || null,
        leave_date: form.leave_date || null,
        notes: form.notes || null,
      };
      await axios.put(`${API}/api/athletes/${athleteId}/seasons/${form.season_id}`, payload, h);
      toast.success('Season link saved.');
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save link');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="col-span-1 md:col-span-2 lg:col-span-3" data-testid="athlete-season-timeline">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Calendar size={14} className="text-emerald-600" />
            Season participation
          </CardTitle>
          {canEdit ? (
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={openNew} data-testid="season-link-new">
              + Add season
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <p className="text-xs text-gray-400 text-center py-4">Loading…</p>
        ) : links.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">No season history yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {links.map((l) => (
              <li
                key={l.id}
                className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-50 dark:border-gray-800 last:border-b-0"
                data-testid={`season-link-${l.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{l.season_name}</p>
                    <Badge className={`${STATUS_STYLES[l.status] || 'bg-gray-100 text-gray-600'} text-[10px]`} data-testid={`season-link-status-${l.id}`}>
                      {l.status}
                    </Badge>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    joined {l.join_date || '—'}{l.leave_date ? ` · left ${l.leave_date}` : ''}
                    {l.notes ? ` · ${l.notes}` : ''}
                  </p>
                </div>
                {canEdit ? (
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => openEdit(l)} data-testid={`season-link-edit-${l.id}`}>
                    <Pencil size={10} />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="season-link-dialog">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add season' : 'Edit participation'}</DialogTitle>
            <DialogDescription className="sr-only">Update the athlete's season status.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Season</Label>
              <Select
                value={form.season_id}
                onValueChange={(v) => setForm({ ...form, season_id: v })}
                disabled={editing !== 'new'}
              >
                <SelectTrigger data-testid="season-link-season-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {seasons.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}{s.is_current ? ' (current)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="season-link-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="returned">Returned</SelectItem>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Join date</Label>
                <DatePicker value={form.join_date} onChange={(v) => setForm({ ...form, join_date: v })} testId="season-link-join-date" />
              </div>
              <div>
                <Label>Leave date</Label>
                <DatePicker value={form.leave_date} onChange={(v) => setForm({ ...form, leave_date: v })} testId="season-link-leave-date" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g., Trial extended 2 weeks" data-testid="season-link-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={submit} disabled={submitting} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="season-link-save">
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SeasonTimeline;
